import React from "react";
import { useNavigate } from "react-router-dom";
import {
  LuBuilding, LuBuilding2, LuCalendarDays, LuCreditCard, LuHospital, LuPackage, LuSend, LuStamp, LuUser, LuUsers,
} from "react-icons/lu";
import { PageLayout } from "../../app/PageLayout";
import { Card } from "../../components/ui";
import { t } from "../../i18n";
import { ClinicSection } from "./sections/ClinicSection";
import { CompanyLinks } from "./sections/CompanyLinks";
import { CustomKits } from "./sections/CustomKits";
import { EmailAccountSection } from "./sections/EmailAccountSection";
import { HospitalLinks } from "./sections/HospitalLinks";
import { PlanDetails } from "./sections/PlanDetails";
import { ProfileForm } from "./sections/ProfileForm";
import { ReservaLinks } from "./sections/ReservaLinks";
import { SecretaryEmails } from "./sections/SecretaryEmails";
import { TemplateUpload } from "./sections/TemplateUpload";
import { useSession } from "../../context/SessionContext";
import { useAccess } from "../../hooks/useAccess";
import { PATHS } from "../../app/paths";

// Atalhos fixos abaixo do cabeçalho: cada um rola até o card com aquele id (aparece na URL, por isso em português).
const SHORTCUTS = [
  { id: "conta", label: t("settings.shortcut.account") },
  { id: "perfil", label: t("settings.shortcut.profile") },
  { id: "documentos", label: t("settings.shortcut.files") },
  { id: "vinculos", label: t("settings.shortcut.links") },
  { id: "email", label: t("settings.shortcut.email") },
];

const ICON_SIZE = 20;

// O #id nativo dispara popstate: o router trata como "voltar" (anima a página e restaura a rolagem).
// Aqui rolamos suave e trocamos o hash sem passar pelo router.
const goToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
  e.preventDefault();
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  document.getElementById(id)?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  window.history.replaceState(window.history.state, "", `#${id}`);
};

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { metadata } = useSession();
  const { canManageClinic, canManageSecretaries, isCourtesy } = useAccess();

  return (
    <PageLayout title={t("settings.title")} onBack={() => navigate(PATHS.newOrder)}>
      <nav
        aria-label={t("settings.shortcuts")}
        className="sticky top-below-header z-10 -mx-4 -my-1 flex gap-2 overflow-x-auto bg-surface px-4 py-2 [scrollbar-width:none]"
      >
        {SHORTCUTS.map(({ id, label }) => (
          <a
            key={id}
            href={`#${id}`}
            onClick={(e) => goToSection(e, id)}
            className="inline-flex min-h-11 shrink-0 items-center rounded-button border border-primary-line bg-surface px-4 text-label font-semibold text-primary transition-colors duration-fast hover:bg-primary-tint"
          >
            {label}
          </a>
        ))}
      </nav>

      <div className="stagger flex flex-col gap-4">
        <Card
          id="conta"
          icon={<LuCreditCard size={ICON_SIZE} />}
          title={t("settings.account.title")}
          description={isCourtesy ? t("settings.account.subtitleClinic") : t("settings.account.subtitle")}
        >
          <PlanDetails specialty={metadata.especialidade} />
        </Card>

        {/* Só plano de clínica (2+ vagas). Avulso = um médico, sem esta seção. */}
        {canManageClinic && (
          <Card icon={<LuBuilding2 size={ICON_SIZE} />} title={t("settings.clinic.title")} description={t("settings.clinic.subtitle")}>
            <ClinicSection />
          </Card>
        )}

        <Card id="perfil" icon={<LuUser size={ICON_SIZE} />} title={t("settings.profile.title")} description={t("settings.profile.subtitle")}>
          <ProfileForm />
        </Card>

        <Card id="documentos" icon={<LuStamp size={ICON_SIZE} />} title={t("settings.files.title")} description={t("settings.files.subtitle")}>
          <div className="grid gap-3 sm:grid-cols-2">
            <TemplateUpload
              folder="receituarios"
              extensions={["pdf"]}
              title={t("settings.files.prescription")}
              hint={t("settings.files.prescriptionHint")}
              uploadLabel={t("settings.files.prescriptionUpload")}
              errorMessage={t("settings.files.prescriptionError")}
              successMessage={t("settings.files.prescriptionSuccess")}
            />
            <TemplateUpload
              folder="carimbos"
              extensions={["png", "jpg", "jpeg"]}
              title={t("settings.files.stamp")}
              hint={t("settings.files.stampHint")}
              uploadLabel={t("settings.files.stampUpload")}
              errorMessage={t("settings.files.stampError")}
              successMessage={t("settings.files.stampSuccess")}
            />
          </div>
        </Card>

        {canManageSecretaries && (
          <Card icon={<LuUsers size={ICON_SIZE} />} title={t("settings.secretaries.title")} description={t("settings.secretaries.subtitle")}>
            <SecretaryEmails />
          </Card>
        )}

        <Card icon={<LuPackage size={ICON_SIZE} />} title={t("settings.kits.title")} description={t("settings.kits.subtitle")}>
          <CustomKits />
        </Card>

        <Card id="vinculos" icon={<LuBuilding size={ICON_SIZE} />} title={t("settings.companies.title")} description={t("settings.companies.subtitle")}>
          <CompanyLinks />
        </Card>

        <Card icon={<LuHospital size={ICON_SIZE} />} title={t("settings.hospitals.title")} description={t("settings.hospitals.subtitle")}>
          <HospitalLinks />
        </Card>

        <Card icon={<LuCalendarDays size={ICON_SIZE} />} title={t("settings.bookings.title")} description={t("settings.bookings.subtitle")}>
          <ReservaLinks />
        </Card>

        <Card
          id="email"
          icon={<LuSend size={ICON_SIZE} />}
          title={t("settings.emailAccount.title")}
          description={t("settings.emailAccount.subtitle")}
        >
          <EmailAccountSection />
        </Card>
      </div>
    </PageLayout>
  );
};
